import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { usePostHog } from '@posthog/react';
import { Button, Callout, Card, Field, Textarea } from '@viniciomendesr/rayrovisk-design-system';
import { useSubmitFeedback } from '../api/queries';
import { IN_DEVELOPMENT, type DevelopmentSurface } from './inDevelopmentCopy';

/**
 * A function that is on the menu and is not built.
 *
 * It deliberately does not describe what the feature will be. Nobody here knows
 * yet, and a screen that promised one is the kind of claim this product spends
 * a lot of effort not making — so the only thing it asserts is that the thing
 * is not ready, and the rest of the screen is a question.
 *
 * Reached by clicking a padlocked entry, which is the shell's existing rule: a
 * locked entry still navigates and the route is what explains.
 */
export function InDevelopmentScreen({ surface }: { surface: DevelopmentSurface }): JSX.Element {
  const copy = IN_DEVELOPMENT[surface];
  const posthog = usePostHog();
  const submitFeedback = useSubmitFeedback();
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Moving between the three without a full navigation keeps the previous
  // screen's answer and its "obrigado" otherwise, which reads as having already
  // written about a feature she has not seen yet.
  useEffect(() => {
    setMessage('');
    setSubmitted(false);
    submitFeedback.reset();
    // `submitFeedback` is a new object each render; the surface is the change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const wanted = message.trim();
    if (wanted === '') {
      return;
    }

    try {
      await submitFeedback.mutateAsync({ topic: 'idea', message: wanted, surface });
      posthog?.capture('feedback_submitted', {
        topic: 'idea',
        message_length: wanted.length,
        surface: `in_development_${surface}`
      });
      setSubmitted(true);
    } catch {
      // The mutation renders its own error below. Success stays tied to the
      // durable write, never to the click.
    }
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 560 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ font: '600 24px/1.2 var(--rvk-font-expressive)', margin: 0 }}>{copy.title}</h1>
        <p
          style={{
            font: '400 14px/1.6 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)',
            margin: 0
          }}
        >
          {copy.lead}
        </p>
      </div>

      <Card layer="functional" padding="lg">
        {submitted ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start' }}>
            <Callout tone="success" live="status">
              {copy.thanks}
            </Callout>
            <Button
              variant="secondary"
              onClick={() => {
                setMessage('');
                setSubmitted(false);
                submitFeedback.reset();
              }}
            >
              Escrever outra
            </Button>
          </div>
        ) : (
          <form onSubmit={(event) => void submit(event)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field className="rvk-hint-above" label={copy.question} hint={copy.hint}>
              <Textarea
                rows={5}
                value={message}
                maxLength={1200}
                onChange={(event) => setMessage(event.target.value)}
              />
            </Field>

            {submitFeedback.isError && (
              <p
                role="alert"
                style={{
                  margin: 0,
                  font: '400 13px/1.5 var(--rvk-font-functional)',
                  color: 'var(--rvk-status-error)'
                }}
              >
                {(submitFeedback.error as Error).message}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                loading={submitFeedback.isPending}
                access={message.trim() === '' ? 'unavailable' : undefined}
                accessReason="Escreva o que você quer aqui primeiro."
              >
                Enviar
              </Button>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}
